import { convertToAttributeSlug } from './commons';

// Native contact fields a CSV column can be mapped to (DataImport::ColumnMapping::NATIVE_FIELDS).
export const NATIVE_IMPORT_FIELDS = [
  'name',
  'phone_number',
  'email',
  'identifier',
  'company',
  'city',
  'labels',
];

// Same header aliases the backend already suggests (DataImport::CsvHeaderValidator::KNOWN_MISTAKES).
const KNOWN_MISTAKES = {
  phone: 'phone_number',
  tags: 'labels',
  tag: 'labels',
  etiqueta: 'labels',
  etiquetas: 'labels',
  marcadores: 'labels',
};

// Keys a new custom field can't take: native fields plus
// CustomAttributeDefinition::STANDARD_ATTRIBUTES[:contact].
const RESERVED_ATTRIBUTE_KEYS = [
  ...NATIVE_IMPORT_FIELDS,
  'country_code',
  'created_at',
  'last_activity_at',
  'referer',
  'blocked',
];

export const PHONE_TARGET = 'field:phone_number';
export const NEW_FIELD_TARGET = 'new';
export const IGNORE_TARGET = 'ignore';

// Header plus a couple of sample rows is all the mapping step needs.
const SAMPLE_BYTES = 64 * 1024;
const LINE_FEED = 10;

// UTF-8 first, falling back to Windows-1252 (common in pt-BR Excel exports), like
// DataImport::CsvParser does on the backend. The UTF-8 decoder also drops the BOM.
const decodeCsvBytes = bytes => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
};

// Keeps only whole lines, so a multi-byte character cut by the slice can't fail the UTF-8 check.
const wholeLines = (bytes, isWholeFile) => {
  if (isWholeFile) return bytes;
  const lastLineFeed = bytes.lastIndexOf(LINE_FEED);
  return lastLineFeed > 0 ? bytes.subarray(0, lastLineFeed + 1) : bytes;
};

export const detectSeparator = text => {
  const [firstLine = ''] = text.split(/\r?\n/, 1);
  const count = char => firstLine.split(char).length - 1;
  return count(';') > count(',') ? ';' : ',';
};

export const parseCsvRecords = (text, separator, limit) => {
  const records = [];
  let record = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length && records.length < limit; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === separator) {
      record.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (records.length < limit && (field || record.length)) {
    record.push(field);
    records.push(record);
  }
  return records;
};

export const readCsvHead = async (file, sampleRows = 2) => {
  const buffer = await file.slice(0, SAMPLE_BYTES).arrayBuffer();
  const bytes = wholeLines(new Uint8Array(buffer), file.size <= SAMPLE_BYTES);
  const text = decodeCsvBytes(bytes);
  const [headers = [], ...samples] = parseCsvRecords(
    text,
    detectSeparator(text),
    sampleRows + 1
  );
  return { headers, samples };
};

const suggestTarget = (header, attributeKeys) => {
  const trimmed = header.trim();
  const lower = trimmed.toLowerCase();
  if (NATIVE_IMPORT_FIELDS.includes(lower)) return `field:${lower}`;
  if (KNOWN_MISTAKES[lower]) return `field:${KNOWN_MISTAKES[lower]}`;
  if (attributeKeys.includes(trimmed)) return `attr:${trimmed}`;
  return IGNORE_TARGET;
};

// One row per CSV column, pre-selected with the matches the import already recognized by
// header name; a destination already taken by an earlier column starts as ignored.
export const buildMappingRows = (headers, samples, attributeKeys) => {
  const taken = new Set();
  return headers.map((header, index) => {
    let target = suggestTarget(header, attributeKeys);
    if (taken.has(target)) target = IGNORE_TARGET;
    if (target !== IGNORE_TARGET) taken.add(target);

    return {
      index,
      header,
      example: samples.map(row => row[index]).find(value => value?.trim()),
      target,
      newFieldName: header.trim(),
    };
  });
};

export const newFieldKey = name =>
  convertToAttributeSlug(
    name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
  );

const destinationOf = row =>
  row.target === NEW_FIELD_TARGET
    ? `attr:${newFieldKey(row.newFieldName)}`
    : row.target;

const newFieldError = (row, attributeKeys) => {
  const key = newFieldKey(row.newFieldName);
  if (!key) return 'NEW_FIELD_NAME_REQUIRED';
  if (RESERVED_ATTRIBUTE_KEYS.includes(key) || attributeKeys.includes(key)) {
    return 'NEW_FIELD_EXISTS';
  }
  return null;
};

// rowErrors maps a column index to an error code; phoneCount must end up being exactly 1.
export const getMappingIssues = (rows, attributeKeys) => {
  const imported = rows.filter(row => row.target !== IGNORE_TARGET);
  const usage = imported.reduce((acc, row) => {
    const destination = destinationOf(row);
    acc[destination] = (acc[destination] || 0) + 1;
    return acc;
  }, {});

  const rowErrors = {};
  imported.forEach(row => {
    const error =
      row.target === NEW_FIELD_TARGET
        ? newFieldError(row, attributeKeys)
        : null;
    if (error) rowErrors[row.index] = error;
    else if (usage[destinationOf(row)] > 1) rowErrors[row.index] = 'DUPLICATE';
  });

  return { rowErrors, phoneCount: usage[PHONE_TARGET] || 0 };
};

const toTarget = row => {
  if (row.target === IGNORE_TARGET) return { type: 'ignore' };
  if (row.target === NEW_FIELD_TARGET) {
    return {
      type: 'new_custom_attribute',
      key: newFieldKey(row.newFieldName),
      display_name: row.newFieldName.trim(),
    };
  }
  const [kind, key] = row.target.split(/:(.*)/);
  return { type: kind === 'field' ? 'field' : 'custom_attribute', key };
};

// The data_imports.column_mapping payload the import endpoint expects.
export const toColumnMapping = rows =>
  rows.map(row => ({
    index: row.index,
    header: row.header,
    target: toTarget(row),
  }));
